<template>
  <div class="fluxo-alocacoes-page">
    <pulse-card
      title="Alocações"
      subtitle="Colaboradores por fluxo e etapa operacional"
    >
      <template #actions>
        <pulse-button
          label="Configurações"
          icon="pi pi-cog"
          severity="secondary"
          outlined
          @click="abrirGerenciamento"
        />
        <pulse-button
          label="Recarregar"
          icon="pi pi-refresh"
          outlined
          :loading="loading"
          @click="carregarTelaInicial"
        />
      </template>

      <div class="filtro-fluxo">
        <pulse-field label="Fluxo">
          <pulse-select
            v-model="fluxoSelecionado"
            :items="fluxos"
            item-text="text"
            item-value="value"
            placeholder="Selecione o fluxo"
            clearable
            :loading="loading"
            @change="selecionarFluxo"
          />
        </pulse-field>
      </div>

      <div v-if="fluxoSelecionado" class="filtro-etapas">
        <span class="filtro-etapas__label">Etapas</span>
        <div class="filtro-etapas__lista">
          <button
            v-for="etapa in etapasDoFluxo"
            :key="etapa.id"
            type="button"
            class="etapa-filtro"
            :class="{ 'etapa-filtro--ativa': etapaFiltroIds.includes(etapa.id) }"
            :style="{ '--etapa-cor': etapa.cor || '#64748b' }"
            @click="alternarFiltroEtapa(etapa)"
          >
            <i
              class="pi"
              :class="etapaFiltroIds.includes(etapa.id) ? 'pi-check' : 'pi-circle-fill'"
            />
            {{ etapa.nome }}
          </button>

          <pulse-button
            v-if="etapaFiltroIds.length"
            label="Limpar"
            icon="pi pi-filter-slash"
            severity="secondary"
            text
            small
            @click="limparFiltroEtapas"
          />
        </div>
      </div>
    </pulse-card>

    <pulse-message
      v-if="mensagem"
      :severity="tipoMensagem"
      closable
      @close="mensagem = ''"
    >
      {{ mensagem }}
    </pulse-message>

    <template v-if="fluxoSelecionado">
      <pulse-card
        :title="`Colaboradores (${colaboradoresDaAlocacao.length})`"
        :subtitle="`${fluxoSelecionadoDescricao} · ${alocacoesFiltradasPorEtapa.length} alocação(ões)`"
      >
        <template #actions>
          <div class="pulse-segmented">
            <pulse-button
              label="Todos"
              icon="pi pi-users"
              text
              :severity="filtroDisponibilidade === 'todos' ? 'primary' : 'secondary'"
              @click="filtroDisponibilidade = 'todos'"
            />
            <pulse-button
              label="Disponíveis"
              icon="pi pi-check-circle"
              text
              :severity="filtroDisponibilidade === 'disponivel' ? 'success' : 'secondary'"
              @click="filtroDisponibilidade = 'disponivel'"
            />
            <pulse-button
              label="Indisponíveis"
              icon="pi pi-times-circle"
              text
              :severity="filtroDisponibilidade === 'indisponivel' ? 'danger' : 'secondary'"
              @click="filtroDisponibilidade = 'indisponivel'"
            />
          </div>
          <pulse-input-text
            v-model="buscaColaborador"
            icon="pi pi-search"
            placeholder="Buscar"
            clearable
            class="busca-tabela"
          />
        </template>

        <v-data-table
          :headers="headersColaboradores"
          :items="colaboradoresDaAlocacao"
          :search="buscaColaborador"
          :items-per-page="10"
          class="pulse-table"
          no-data-text="Nenhum colaborador vinculado."
        >
          <template v-slot:item.nome="{ item }">
            <span class="nome-colaborador">{{ item.nome }}</span>
          </template>
          <template v-slot:item.escalaHojeResumo="{ item }">
            <div class="d-flex align-center">
              <div class="escala-resumo">
                {{ item.escalaHojeResumo || "Sem escala" }}
              </div>
              <pulse-button
                icon="pi pi-calendar-clock"
                text
                rounded
                small
                title="Ver escala completa"
                class="ml-1"
                @click.stop="abrirDialogEscala(item)"
              />
            </div>
          </template>
          <template v-slot:item.statusDisponibilidade="{ item }">
            <colaborador-status-disponibilidade :status="item.statusInfo" />
          </template>
        </v-data-table>
      </pulse-card>

      <pulse-card
        :title="`Tickets funerários (${totalTicketsFunerarios})`"
        :subtitle="`Em aberto · tickets agendados e em andamento do fluxo ${fluxoSelecionadoDescricao.toLowerCase()}`"
      >
        <template #actions>
          <pulse-button
            :label="`Atrasados (${ticketsAtrasadosCount})`"
            icon="pi pi-exclamation-circle"
            :severity="filtroTicketsAtrasados ? 'danger' : 'secondary'"
            :outlined="!filtroTicketsAtrasados"
            @click="filtroTicketsAtrasados = !filtroTicketsAtrasados"
          />
          <pulse-input-text
            v-model="buscaTicket"
            icon="pi pi-search"
            placeholder="Buscar"
            clearable
            class="busca-tabela"
          />
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" class="filtros-tickets-switch" v-on="on">
                <v-switch
                  v-model="filtroSomenteEtapasSemAtribuicao"
                  label="Sem atribuição"
                  color="#6366f1"
                  dense
                  inset
                  hide-details
                  class="mt-0 pt-0"
                ></v-switch>
              </div>
            </template>
            <span>Exibir somente etapas sem atribuições</span>
          </v-tooltip>
        </template>

        <v-progress-linear
          v-if="loadingTicketsFunerarios"
          indeterminate
          color="#6366f1"
          class="mb-3"
        ></v-progress-linear>

        <pulse-message v-if="fluxoSelecionado !== 'funeraria'">
          A listagem de tickets está disponível para o fluxo Funerário.
        </pulse-message>

        <pulse-message
          v-else-if="
            !loadingTicketsFunerarios &&
            !ticketsFunerariosFiltrados.length &&
            !ticketsFavoritosFiltrados.length
          "
        >
          Nenhum ticket funerário não concluído encontrado nos últimos 7 dias.
        </pulse-message>

        <div v-else class="tickets-alocacoes-list">
          <div
            v-for="secao in secoesTicketsAlocacao"
            :key="`secao-${secao.chave}`"
            class="secao-tickets"
            :class="`secao-tickets--${secao.chave}`"
          >
            <div v-if="secao.titulo" class="secao-tickets-titulo">
              <v-icon small :color="secao.cor" class="mr-2">{{
                secao.icone
              }}</v-icon>
              <span>{{ secao.titulo }}</span>
              <pulse-tag
                severity="secondary"
                rounded
                class="ml-2"
                :value="secao.itens.length"
              />
              <div
                v-if="secao.chave !== 'favoritos'"
                class="secao-tickets-linha"
              ></div>
            </div>
            <v-expansion-panels flat class="tickets-panels">
              <v-expansion-panel
                v-for="ticketInfo in secao.itens"
                :key="ticketInfo.ticket.id"
                class="ticket-panel"
                :style="`border-left: 4px solid ${ticketInfo.ticket.etapa.cor}`"
              >
                <v-expansion-panel-header class="ticket-panel__header">
                  <div class="ticket-card-header mr-2">
                    <div class="ticket-card-title">
                      <span class="ticket-id">#{{
                        ticketInfo.ticket.idAtendimento || ticketInfo.ticket.id
                      }}</span>
                      <strong class="ticket-falecido">{{
                        ticketInfo.ticket.nomeFalecido || "-"
                      }}</strong>
                      <v-progress-circular
                        v-if="favoritandoId === ticketInfo.ticket.idAtendimento"
                        indeterminate
                        size="14"
                        width="2"
                        color="#f59e0b"
                      ></v-progress-circular>
                      <button
                        v-else
                        type="button"
                        class="ticket-icon-btn"
                        :class="{ 'ticket-icon-btn--favorito': ehFavorito(ticketInfo.ticket) }"
                        :title="
                          ehFavorito(ticketInfo.ticket)
                            ? 'Remover dos favoritos'
                            : 'Adicionar aos favoritos'
                        "
                        @click.stop="alternarFavorito(ticketInfo.ticket)"
                      >
                        <i
                          class="pi"
                          :class="ehFavorito(ticketInfo.ticket) ? 'pi-star-fill' : 'pi-star'"
                        />
                      </button>
                      <button
                        v-if="ehFavorito(ticketInfo.ticket)"
                        type="button"
                        class="ticket-icon-btn"
                        title="Compartilhar favorito"
                        @click.stop="abrirDialogCompartilharFavorito(ticketInfo.ticket)"
                      >
                        <i class="pi pi-share-alt" />
                      </button>
                      <pulse-button
                        v-if="ticketAgendado(ticketInfo.ticket)"
                        label="Iniciar"
                        icon="pi pi-play-circle"
                        outlined
                        small
                        :loading="
                          avancandoTicketId ===
                          (ticketInfo.ticket.idAtendimento ||
                            ticketInfo.ticket.id)
                        "
                        :disabled="!!avancandoTicketId"
                        @click.stop="avancarTicketAgendado(ticketInfo)"
                      />
                    </div>

                    <div class="ticket-card-sla">
                      <pulse-tag
                        :severity="ticketInfo.status.slaEstourado ? 'danger' : 'success'"
                        :icon="ticketInfo.status.slaEstourado ? 'pi pi-exclamation-circle' : 'pi pi-clock'"
                        :value="
                          ticketInfo.status.slaEstourado
                            ? `Atrasado ${formatarTempoDecorrido(
                                ticketInfo.status.atrasoMinutos,
                              )}`
                            : `Restante ${formatarTempoDecorrido(
                                ticketInfo.status.tempoRestanteMinutos,
                              )}`
                        "
                      />
                    </div>
                  </div>
                </v-expansion-panel-header>

                <v-expansion-panel-content>
                  <div class="ticket-info-grid">
                    <div>
                      <span class="ticket-label">Etapa atual</span>
                      <div class="ticket-value">
                        <pulse-tag
                          :color="
                            ticketInfo.ticket.etapa && ticketInfo.ticket.etapa.cor
                              ? ticketInfo.ticket.etapa.cor
                              : '#607D8B'
                          "
                          :value="
                            ticketInfo.ticket.etapa && ticketInfo.ticket.etapa.nome
                              ? ticketInfo.ticket.etapa.nome
                              : 'Sem etapa'
                          "
                        />
                      </div>
                    </div>
                    <div>
                      <span class="ticket-label">Atendimento</span>
                      <div class="ticket-value">
                        {{ ticketInfo.ticket.dataAtendimento || "-" }}
                      </div>
                    </div>
                    <div>
                      <span class="ticket-label">Contrato</span>
                      <div class="ticket-value">
                        {{ ticketInfo.ticket.contrato || "-" }}
                      </div>
                    </div>
                    <div>
                      <span class="ticket-label">Tipo contrato</span>
                      <div class="ticket-value">
                        {{ ticketInfo.ticket.tipoContrato || "-" }}
                      </div>
                    </div>
                    <div>
                      <span class="ticket-label">Colaboradores</span>
                      <div class="ticket-value ticket-value--tags">
                        <template
                          v-if="obterColaboradoresEtapaAtual(ticketInfo.ticket).length"
                        >
                          <pulse-tag
                            v-for="colab in obterColaboradoresEtapaAtual(ticketInfo.ticket)"
                            :key="colab.id"
                            severity="secondary"
                            icon="pi pi-user"
                            rounded
                            :value="colab.nome"
                          />
                        </template>
                        <span v-else class="ticket-vazio">Nenhum</span>
                      </div>
                    </div>
                    <div>
                      <span class="ticket-label">SLA da etapa</span>
                      <div class="ticket-value">
                        {{ formatarSla(ticketInfo.ticket.etapa) }}
                      </div>
                    </div>
                    <div>
                      <span class="ticket-label">Tempo decorrido</span>
                      <div
                        class="ticket-value ticket-value--forte"
                        :class="{
                          'ticket-value--erro': ticketInfo.status.slaEstourado,
                          'ticket-value--alerta':
                            !ticketInfo.status.slaEstourado &&
                            ticketInfo.status.tempoRestanteMinutos < 30,
                        }"
                      >
                        {{
                          formatarTempoDecorrido(
                            ticketInfo.status.tempoDecorridoMinutos,
                          )
                        }}
                      </div>
                    </div>
                    <div>
                      <span class="ticket-label">Início da etapa</span>
                      <div class="ticket-value">
                        {{
                          ticketInfo.status.inicio
                            ? formatarDataHora(ticketInfo.status.inicio)
                            : "-"
                        }}
                      </div>
                    </div>
                  </div>

                  <div class="ticket-fluxo-titulo">Fluxo de etapas</div>
                  <div class="d-flex justify-center">
                    <ticket-funerario-etapas-timeline
                      :ticket="ticketInfo.ticket"
                      :status="ticketInfo.status"
                      :etapas-fluxo="etapasFunerariasEmAndamento"
                      :somente-sem-atribuicao="filtroSomenteEtapasSemAtribuicao"
                      @selecionar-etapa="abrirDialogAtribuicao(ticketInfo, $event)"
                    />
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>

          <div class="d-flex align-center justify-center pt-1">
            <v-pagination
              v-if="totalPaginasTickets > 1"
              v-model="paginaTicketsVisual"
              :length="totalPaginasTickets"
              :total-visible="5"
              class="pulse-paginator"
              @input="aoAlterarPaginaTickets"
            ></v-pagination>
          </div>
        </div>
      </pulse-card>
    </template>

    <pulse-message v-else>
      Selecione um fluxo para visualizar alocações, etapas e colaboradores.
    </pulse-message>

    <v-dialog v-model="dialogEscala" max-width="620">
      <v-card class="pulse-dialog">
        <pulse-dialog-header
          title="Escala completa"
          icon="pi pi-calendar-clock"
          :subtitle="colaboradorEscalaSelecionado ? colaboradorEscalaSelecionado.nome : ''"
          @close="fecharDialogEscala"
        />

        <v-card-text class="pulse-dialog__body">
          <v-simple-table
            v-if="escalaCompletaSelecionada.length"
            class="pulse-table"
          >
            <thead>
              <tr>
                <th>Dia</th>
                <th>1º período</th>
                <th>2º período</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(escala, index) in escalaCompletaSelecionada"
                :key="`${escala.dia}-${index}`"
              >
                <td>{{ escala.diaDescricao || "-" }}</td>
                <td>
                  {{ formatarPeriodoEscala(escala.entrada1, escala.saida1) }}
                </td>
                <td>
                  {{ formatarPeriodoEscala(escala.entrada2, escala.saida2) }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <pulse-message v-else>
            Nenhuma escala cadastrada para este colaborador.
          </pulse-message>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogAtribuicao" max-width="1200">
      <v-card class="pulse-dialog">
        <pulse-dialog-header
          title="Atribuir colaborador"
          icon="pi pi-user-plus"
          :subtitle="
            ticketAtribuicao
              ? `#${ticketAtribuicao.idAtendimento || ticketAtribuicao.id} - ${
                  ticketAtribuicao.nomeFalecido || ''
                }`
              : ''
          "
          :close-disabled="atribuindoColaborador"
          @close="fecharDialogAtribuicao"
        />

        <v-card-text class="pulse-dialog__body">
          <div
            v-if="ticketAtribuicao && ticketAtribuicao.etapa"
            class="atribuicao-resumo"
          >
            <div class="mb-4">
              <pulse-tag
                :color="ticketAtribuicao.etapa.cor || '#607D8B'"
                :value="ticketAtribuicao.etapa.nome"
              />
            </div>

            <div class="atribuicao-datas">
              <pulse-field label="Previsão de início">
                <pulse-input-text
                  v-model="previsaoInicioAtribuicao"
                  type="datetime-local"
                />
              </pulse-field>
              <pulse-field label="Previsão de fim">
                <pulse-input-text
                  :value="previsaoFimAtribuicaoExibicao"
                  readonly
                />
              </pulse-field>
            </div>

            <div class="atribuidos-header">
              <span class="atribuidos-titulo">Colaboradores atribuídos</span>
              <pulse-tag
                severity="secondary"
                rounded
                :value="`${colaboradoresAtuaisAtribuicao.length} selecionado(s)`"
              />
            </div>
            <div class="area-colaboradores-atribuidos">
              <pulse-tag
                v-for="colaborador in colaboradoresAtuaisAtribuicao"
                :key="colaborador.id"
                severity="primary"
                icon="pi pi-user"
                rounded
              >
                {{ colaborador.nome }}
                <button
                  type="button"
                  class="pulse-tag__remove"
                  title="Remover"
                  @click="removerColaboradorAtribuicao(colaborador.id)"
                >
                  <i class="pi pi-times" />
                </button>
              </pulse-tag>
              <div
                v-if="!colaboradoresAtuaisAtribuicao.length"
                class="atribuidos-vazio"
              >
                Nenhum colaborador atribuído
              </div>
            </div>
          </div>

          <div class="card-secao-atribuicao">
            <div class="secao-atribuicao-header">
              <div>
                <div class="secao-atribuicao-titulo">
                  Selecionar colaboradores para atribuir
                </div>
                <div class="secao-atribuicao-subtitulo">
                  Escolha um ou mais colaboradores disponíveis para esta etapa
                </div>
              </div>
            </div>

            <div class="secao-atribuicao-filtros">
              <div class="pulse-segmented">
                <pulse-button
                  label="Todos"
                  icon="pi pi-users"
                  text
                  :severity="filtroAtribuicaoDisponibilidade === 'todos' ? 'primary' : 'secondary'"
                  @click="filtroAtribuicaoDisponibilidade = 'todos'"
                />
                <pulse-button
                  label="Disponíveis"
                  icon="pi pi-check-circle"
                  text
                  :severity="filtroAtribuicaoDisponibilidade === 'disponivel' ? 'success' : 'secondary'"
                  @click="filtroAtribuicaoDisponibilidade = 'disponivel'"
                />
                <pulse-button
                  label="Indisponíveis"
                  icon="pi pi-times-circle"
                  text
                  :severity="filtroAtribuicaoDisponibilidade === 'indisponivel' ? 'danger' : 'secondary'"
                  @click="filtroAtribuicaoDisponibilidade = 'indisponivel'"
                />
              </div>
              <pulse-input-text
                v-model="buscaColaboradorAtribuicao"
                icon="pi pi-search"
                placeholder="Buscar colaborador"
                clearable
                class="busca-tabela"
              />
            </div>

            <v-data-table
              :headers="headersAtribuicaoColaboradores"
              :items="colaboradoresAtribuicao"
              :search="buscaColaboradorAtribuicao"
              :items-per-page="5"
              class="pulse-table"
              no-data-text="Nenhum colaborador vinculado a esta etapa."
            >
              <template v-slot:item.nome="{ item }">
                <span class="nome-colaborador nome-colaborador-truncado" :title="item.nome">
                  {{ item.nome }}
                </span>
              </template>
              <template v-slot:item.escalaHojeResumo="{ item }">
                <div class="d-flex align-center">
                  <div class="escala-resumo">
                    {{ item.escalaHojeResumo || "Sem escala" }}
                  </div>
                  <pulse-button
                    icon="pi pi-calendar-clock"
                    text
                    rounded
                    small
                    title="Ver escala completa"
                    class="ml-1"
                    @click.stop="abrirDialogEscala(item)"
                  />
                </div>
              </template>
              <template v-slot:item.statusDisponibilidade="{ item }">
                <colaborador-status-disponibilidade :status="item.statusInfo" />
              </template>
              <template v-slot:item.acoes="{ item }">
                <pulse-button
                  small
                  :severity="colaboradorSelecionadoAtribuicao(item.id) ? 'success' : 'primary'"
                  :outlined="colaboradorSelecionadoAtribuicao(item.id)"
                  :disabled="atribuindoColaborador || item.emFerias"
                  @click="alternarColaboradorAtribuicao(item)"
                >
                  <v-icon small>{{ iconeAcaoAtribuicao(item) }}</v-icon>
                  {{ textoAcaoAtribuicao(item) }}
                </pulse-button>
              </template>
            </v-data-table>
          </div>
        </v-card-text>

        <div class="pulse-dialog__footer pulse-dialog__footer--bordered">
          <pulse-button
            label="Cancelar"
            severity="secondary"
            text
            :disabled="atribuindoColaborador"
            @click="fecharDialogAtribuicao"
          />
          <pulse-button
            label="Salvar atribuição"
            icon="pi pi-save"
            :loading="atribuindoColaborador"
            @click="salvarAtribuicaoEtapa"
          />
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog" persistent max-width="1100">
      <v-card class="pulse-dialog">
        <pulse-dialog-header
          :title="editando ? 'Editar alocação' : 'Adicionar alocação'"
          icon="pi pi-sitemap"
          :close-disabled="salvando"
          @close="fecharDialog"
        />

        <v-card-text class="pulse-dialog__body">
          <v-form ref="formAlocacao" v-model="formValido" lazy-validation>
            <pulse-message class="mb-4">
              Para salvar, preencha os dados da alocação e selecione pelo
              menos uma etapa e um colaborador.
            </pulse-message>

            <div class="form-alocacao-grid">
              <pulse-field label="Nome">
                <pulse-input-text
                  v-model="form.nome"
                  placeholder="Nome da alocação"
                  hide-details="auto"
                  :rules="[obrigatorio]"
                  :disabled="salvando"
                />
              </pulse-field>
              <pulse-field label="Fluxo">
                <pulse-select
                  v-model="form.fluxo"
                  :items="fluxos"
                  item-text="text"
                  item-value="value"
                  placeholder="Selecione o fluxo"
                  hide-details="auto"
                  :rules="[obrigatorio]"
                  :disabled="salvando"
                  @change="aoAlterarFluxo"
                />
              </pulse-field>
            </div>

            <v-tabs v-model="dialogTab" grow class="pulse-tabs mt-4">
              <v-tab>Etapas</v-tab>
              <v-tab :disabled="!form.etapaIds.length">Colaboradores</v-tab>
            </v-tabs>

            <v-tabs-items v-model="dialogTab" class="dialog-tabs-items mt-3">
              <v-tab-item>
                <div class="dialog-tab-panel">
                  <div class="dialog-tab-titulo">Etapas em andamento</div>
                  <v-data-table
                    v-model="etapasSelecionadas"
                    :headers="headersEtapas"
                    :items="etapas"
                    item-key="id"
                    show-select
                    :loading="loadingEtapas"
                    :items-per-page="8"
                    class="pulse-table"
                    no-data-text="Selecione um fluxo para carregar as etapas."
                  >
                    <template v-slot:item.cor="{ item }">
                      <span
                        class="swatch"
                        :style="{ backgroundColor: item.cor || '#B0BEC5' }"
                      ></span>
                    </template>
                  </v-data-table>
                </div>
              </v-tab-item>

              <v-tab-item>
                <div class="dialog-tab-panel">
                  <div class="dialog-tab-titulo">Colaboradores ativos</div>
                  <pulse-input-text
                    v-if="form.etapaIds.length"
                    v-model="buscaDialogColaborador"
                    icon="pi pi-search"
                    placeholder="Buscar colaborador"
                    clearable
                    class="mb-3"
                  />

                  <v-data-table
                    v-if="form.etapaIds.length"
                    v-model="colaboradoresSelecionados"
                    :headers="headersDialogColaboradores"
                    :items="colaboradoresAtivos"
                    :search="buscaDialogColaborador"
                    item-key="id"
                    show-select
                    :loading="loadingColaboradores"
                    :items-per-page="8"
                    class="pulse-table"
                    no-data-text="Nenhum colaborador ativo encontrado."
                  >
                    <template v-slot:item.escalaResumo="{ item }">
                      <div class="escala-resumo">
                        {{ item.escalaResumo || "Sem escala" }}
                      </div>
                    </template>
                  </v-data-table>

                  <pulse-message v-else class="mt-4">
                    Selecione uma ou mais etapas para escolher colaboradores.
                  </pulse-message>
                </div>
              </v-tab-item>
            </v-tabs-items>
          </v-form>
        </v-card-text>

        <div class="pulse-dialog__footer">
          <span class="pulse-dialog__footer-info">
            {{ form.etapaIds.length }} etapa(s) e
            {{ form.colaboradorIds.length }} colaborador(es) selecionado(s)
          </span>
          <pulse-button
            label="Cancelar"
            severity="secondary"
            text
            :disabled="salvando"
            @click="fecharDialog"
          />
          <pulse-button
            label="Salvar"
            icon="pi pi-save"
            :loading="salvando"
            @click="salvar"
          />
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogGerenciar" persistent max-width="1200">
      <v-card class="pulse-dialog">
        <pulse-dialog-header
          title="Configurações"
          subtitle="Alocações cadastradas por fluxo"
          icon="pi pi-cog"
          :close-disabled="loadingGerenciamento"
          @close="dialogGerenciar = false"
        />

        <v-card-text class="pulse-dialog__body">
          <div class="gerenciamento-toolbar">
            <pulse-input-text
              v-model="buscaGerenciamento"
              icon="pi pi-search"
              placeholder="Buscar alocação"
              clearable
              class="busca-tabela"
            />
            <div class="gerenciamento-toolbar__acoes">
              <pulse-button
                icon="pi pi-refresh"
                severity="secondary"
                outlined
                title="Atualizar listagem"
                :loading="loadingGerenciamento"
                @click="carregarAlocacoesGerenciamento"
              />
              <pulse-button
                label="Adicionar"
                icon="pi pi-plus"
                :disabled="loading"
                @click="abrirDialogAdicionar"
              />
            </div>
          </div>

          <v-data-table
            :headers="headersGerenciamento"
            :items="alocacoesGerenciamentoFormatadas"
            :search="buscaGerenciamento"
            :loading="loadingGerenciamento"
            :items-per-page="10"
            class="pulse-table"
            no-data-text="Nenhuma alocação cadastrada."
          >
            <template v-slot:item.etapasResumo="{ item }">
              <div class="escala-resumo">
                {{ item.etapasResumo || "Sem etapas" }}
              </div>
            </template>

            <template v-slot:item.acoes="{ item }">
              <div class="d-flex justify-end">
                <pulse-button
                  icon="pi pi-pencil"
                  severity="secondary"
                  text
                  rounded
                  title="Editar"
                  @click="abrirDialogEditar(item)"
                />
                <pulse-button
                  icon="pi pi-trash"
                  severity="danger"
                  text
                  rounded
                  title="Excluir"
                  @click="confirmarExclusao(item)"
                />
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogExcluir" persistent max-width="430">
      <v-card class="pulse-dialog">
        <pulse-dialog-header
          title="Excluir alocação"
          icon="pi pi-trash"
          :closable="false"
        />
        <v-card-text class="pulse-dialog__body">
          Deseja excluir a alocação
          {{
            alocacaoParaExcluir ? alocacaoParaExcluir.nome : "selecionada"
          }}?
        </v-card-text>
        <div class="pulse-dialog__footer">
          <pulse-button
            label="Cancelar"
            severity="secondary"
            text
            :disabled="excluindo"
            @click="dialogExcluir = false"
          />
          <pulse-button
            label="Inativar"
            severity="danger"
            :loading="excluindo"
            @click="excluir"
          />
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogConfirmacaoHoraExtra" persistent max-width="480">
      <v-card class="pulse-dialog">
        <pulse-dialog-header
          title="Possível hora extra"
          icon="pi pi-exclamation-triangle"
          :closable="false"
        />
        <v-card-text class="pulse-dialog__body">
          <p class="mb-2">
            <strong>{{ confirmacaoHoraExtra.colaborador?.nome }}</strong>
            pode gerar horas extras nesta atividade:
          </p>
          <ul class="mb-3">
            <li
              v-for="motivo in confirmacaoHoraExtra.motivos"
              :key="motivo"
            >
              {{ motivo }}
            </li>
          </ul>
          <p class="mb-0">Deseja adicionar mesmo assim?</p>
        </v-card-text>
        <div class="pulse-dialog__footer">
          <pulse-button
            label="Cancelar"
            severity="secondary"
            text
            @click="fecharConfirmacaoHoraExtra"
          />
          <pulse-button
            label="Adicionar"
            severity="warn"
            @click="confirmarAdicaoComHoraExtra"
          />
        </div>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="dialogCompartilharFavorito"
      persistent
      max-width="600"
    >
      <v-card class="pulse-dialog">
        <pulse-dialog-header
          title="Compartilhar favorito"
          icon="pi pi-share-alt"
          :closable="false"
        />
        <v-card-text class="pulse-dialog__body">
          <p class="mb-4">
            Selecione quem receberá o atendimento
            <strong>#{{ ticketCompartilhamento?.idAtendimento }}</strong>
            nos favoritos.
          </p>
          <pulse-field label="Usuários ativos">
            <v-autocomplete
              v-model="usuariosSelecionadosCompartilhamento"
              :search-input.sync="buscaUsuarioCompartilhamento"
              :items="usuariosCompartilhamento"
              item-text="nome"
              item-value="id"
              placeholder="Buscar usuário"
              class="pulse-input pulse-select"
              :menu-props="{ offsetY: true, contentClass: 'pulse-select-menu' }"
              outlined
              dense
              hide-details
              clearable
              multiple
              chips
              small-chips
              deletable-chips
              :loading="carregandoUsuariosCompartilhamento"
              :disabled="
                carregandoUsuariosCompartilhamento || compartilhandoFavorito
              "
              no-data-text="Nenhum usuário ativo encontrado"
              @change="buscaUsuarioCompartilhamento = null"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.nome }}</v-list-item-title>
                  <v-list-item-subtitle
                    v-if="item.cargo || item.departamento"
                  >
                    {{ [item.cargo, item.departamento].filter(Boolean).join(" - ") }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </pulse-field>
        </v-card-text>
        <div class="pulse-dialog__footer">
          <pulse-button
            label="Cancelar"
            severity="secondary"
            text
            :disabled="compartilhandoFavorito"
            @click="fecharDialogCompartilharFavorito"
          />
          <pulse-button
            label="Compartilhar"
            icon="pi pi-share-alt"
            :loading="compartilhandoFavorito"
            :disabled="
              carregandoUsuariosCompartilhamento ||
              !usuariosSelecionadosCompartilhamento.length
            "
            @click="compartilharFavorito"
          />
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import FluxoAlocacoesService from "../../services/fluxoAlocacoes";
import FunerariaService from "../../services/funeraria";
import UsuarioService from "../../services/usuario";
import TicketFunerarioEtapasTimeline from "./components/TicketFunerarioEtapasTimeline.vue";
import ColaboradorStatusDisponibilidade from "./components/ColaboradorStatusDisponibilidade.vue";
import {
  avaliarAtividadeNaEscala,
  obterPrioridadeOrdenacao,
  resolverDisponibilidade,
} from "./utils/disponibilidadeColaborador";

export default {
  name: "FluxoAlocacoes",

  components: {
    TicketFunerarioEtapasTimeline,
    ColaboradorStatusDisponibilidade,
  },

  data() {
    return {
      loading: false,
      salvando: false,
      excluindo: false,
      loadingEtapas: false,
      loadingColaboradores: false,
      loadingGerenciamento: false,
      loadingTicketsFunerarios: false,
      loadingOcupacoesColaboradores: false,
      atribuindoColaborador: false,
      avancandoTicketId: null,
      dialog: false,
      dialogGerenciar: false,
      dialogExcluir: false,
      dialogEscala: false,
      dialogAtribuicao: false,
      dialogConfirmacaoHoraExtra: false,
      dialogCompartilharFavorito: false,
      confirmacaoHoraExtra: { colaborador: null, motivos: [] },
      dialogTab: 0,
      editando: false,
      formValido: false,
      mensagem: "",
      tipoMensagem: "info",
      alocacoes: [],
      alocacoesGerenciamento: [],
      ticketsFunerarios: [],
      ticketsFavoritos: [],
      favoritandoId: null,
      ticketCompartilhamento: null,
      usuariosCompartilhamento: [],
      usuariosSelecionadosCompartilhamento: [],
      buscaUsuarioCompartilhamento: null,
      carregandoUsuariosCompartilhamento: false,
      compartilhandoFavorito: false,
      ocupacoesColaboradores: [],
      paginaTickets: 0,
      paginaTicketsVisual: 1,
      tamanhoPaginaTickets: 10,
      totalPaginasTickets: 0,
      totalTicketsFunerarios: 0,
      alocacaoParaExcluir: null,
      ticketAtribuicao: null,
      colaboradorAtribuindoId: null,
      previsaoInicioAtribuicao: "",
      colaboradoresAtribuicaoSelecionados: [],
      fluxoSelecionado: "funeraria",
      etapaFiltroIds: [],
      etapas: [],
      colaboradoresAtivos: [],
      etapasSelecionadas: [],
      colaboradoresSelecionados: [],
      colaboradorEscalaSelecionado: null,
      buscaColaborador: "",
      buscaDialogColaborador: "",
      buscaGerenciamento: "",
      buscaTicket: "",
      buscaTicketTimeout: null,
      buscaColaboradorAtribuicao: "",
      filtroDisponibilidade: "todos",
      filtroAtribuicaoDisponibilidade: "todos",
      filtroTicketsAtrasados: false,
      filtroSomenteEtapasSemAtribuicao: false,
      disponibilidadeAtualizacao: 0,
      ticketSlaAtualizacao: 0,
      intervaloDisponibilidade: null,
      intervaloTicketSla: null,
      fluxos: [
        { text: "Funerário", value: "funeraria" },
        /* { text: "Cemiterial", value: "cemiterial" },
        { text: "Mensalista", value: "vaso" }, */
      ],
      form: {
        id: null,
        nome: "",
        fluxo: null,
        etapaIds: [],
        colaboradorIds: [],
      },
      headersColaboradores: [
        { text: "Colaborador", value: "nome" },
        { text: "Função", value: "cargo", width: "180px" },
        { text: "Departamento", value: "departamento", width: "180px" },
        {
          text: "Escala",
          value: "escalaHojeResumo",
          width: "260px",
          sortable: false,
        },
        {
          text: "Status",
          value: "statusDisponibilidade",
          width: "260px",
          sortable: false,
        },
      ],
      headersAtribuicaoColaboradores: [
        { text: "Colaborador", value: "nome", width: "220px" },
        {
          text: "Escala",
          value: "escalaHojeResumo",
          width: "240px",
          sortable: false,
        },
        {
          text: "Status",
          value: "statusDisponibilidade",
          width: "420px",
          sortable: false,
        },
        {
          text: "Ação",
          value: "acoes",
          width: "140px",
          sortable: false,
          align: "center",
        },
      ],
      headersEtapas: [
        { text: "", value: "cor", width: "42px", sortable: false },
        { text: "Etapa", value: "nome" },
        { text: "Ordem", value: "ordem", width: "90px" },
      ],
      headersDialogColaboradores: [
        { text: "Colaborador", value: "nome" },
        { text: "Função", value: "cargo", width: "160px" },
        { text: "Departamento", value: "departamento", width: "170px" },
        {
          text: "Escala",
          value: "escalaResumo",
          width: "320px",
          sortable: false,
        },
      ],
      headersGerenciamento: [
        { text: "Nome", value: "nome" },
        { text: "Fluxo", value: "fluxoDescricao", width: "140px" },
        {
          text: "Etapas",
          value: "etapasResumo",
          width: "280px",
          sortable: false,
        },
        {
          text: "Colaboradores",
          value: "quantidadeColaboradores",
          width: "130px",
        },
        {
          text: "Ações",
          value: "acoes",
          width: "130px",
          sortable: false,
          align: "center",
        },
      ],
    };
  },

  computed: {
    fluxoSelecionadoDescricao() {
      const fluxo = this.fluxos.find(
        (item) => item.value === this.fluxoSelecionado,
      );
      return fluxo?.text || "";
    },
    alocacoesDoFluxo() {
      if (!this.fluxoSelecionado) return [];
      return this.alocacoes.filter(
        (item) => item.fluxo === this.fluxoSelecionado,
      );
    },
    etapasDoFluxo() {
      const etapasPorId = new Map();
      this.alocacoesDoFluxo.forEach((alocacao) => {
        (alocacao.etapas || []).forEach((etapa) => {
          if (!etapasPorId.has(etapa.id)) {
            etapasPorId.set(etapa.id, etapa);
          }
        });
      });
      return Array.from(etapasPorId.values()).sort((a, b) => {
        const ordemA =
          a.ordem === null || a.ordem === undefined ? 999 : a.ordem;
        const ordemB =
          b.ordem === null || b.ordem === undefined ? 999 : b.ordem;
        if (ordemA !== ordemB) return ordemA - ordemB;
        return String(a.nome || "").localeCompare(String(b.nome || ""));
      });
    },
    etapasFunerariasEmAndamento() {
      return (this.etapas || [])
        .filter((etapa) => {
          const tipo = String(etapa.tipo || "").toLowerCase();
          return tipo === "em andamento" && etapa.etapaSuporte !== true;
        })
        .sort((a, b) => {
          const ordemA =
            a.ordem === null || a.ordem === undefined ? 999 : a.ordem;
          const ordemB =
            b.ordem === null || b.ordem === undefined ? 999 : b.ordem;
          if (ordemA !== ordemB) return ordemA - ordemB;
          return String(a.nome || "").localeCompare(String(b.nome || ""));
        });
    },
    alocacoesFiltradasPorEtapa() {
      if (!this.etapaFiltroIds.length) {
        return this.alocacoesDoFluxo;
      }

      return this.alocacoesDoFluxo.filter((alocacao) =>
        (alocacao.etapas || []).some((etapa) =>
          this.etapaFiltroIds.includes(etapa.id),
        ),
      );
    },
    escalaCompletaSelecionada() {
      return [...(this.colaboradorEscalaSelecionado?.escala || [])]
        .filter((e) => !e.inativa)
        .sort((a, b) => {
          const diaA = a.dia === null || a.dia === undefined ? 999 : a.dia;
          const diaB = b.dia === null || b.dia === undefined ? 999 : b.dia;
          return diaA - diaB;
        });
    },
    ocupacoesPorColaborador() {
      const ocupacoes = new Map();
      (this.ocupacoesColaboradores || []).forEach((ocupacao) => {
        if (ocupacao && ocupacao.colaboradorId) {
          ocupacoes.set(Number(ocupacao.colaboradorId), ocupacao);
        }
      });
      return ocupacoes;
    },
    colaboradoresAtribuicao() {
      const etapaId = Number(this.ticketAtribuicao?.etapa?.id);
      if (!Number.isFinite(etapaId)) return [];

      const colaboradoresPorId = new Map();
      this.alocacoesDoFluxo
        .filter((alocacao) =>
          (alocacao.etapas || []).some((etapa) => Number(etapa.id) === etapaId),
        )
        .forEach((alocacao) => {
          (alocacao.colaboradores || []).forEach((colaborador) => {
            if (!colaboradoresPorId.has(colaborador.id)) {
              colaboradoresPorId.set(colaborador.id, colaborador);
            }
          });
        });

      const janelaAtividade = this.janelaAtividadeAtribuicao;
      return Array.from(colaboradoresPorId.values())
        .map((colaborador) =>
          this.montarStatusColaborador(
            colaborador,
            janelaAtividade,
            this.disponibilidadeAtualizacao,
          ),
        )
        .filter((colaborador) => {
          if (this.filtroAtribuicaoDisponibilidade === "disponivel")
            return !colaborador.indisponivel;
          if (this.filtroAtribuicaoDisponibilidade === "indisponivel")
            return colaborador.indisponivel;
          return true;
        })
        .map((colaborador) => ({
          colaborador,
          selecionado: this.colaboradorSelecionadoAtribuicao(colaborador.id)
            ? 0
            : 1,
          prioridade: obterPrioridadeOrdenacao(colaborador.statusInfo, {
            emFerias: !!colaborador.emFerias,
          }),
          fimOcupacao:
            this.obterFimOcupacao(
              this.obterOcupacaoColaborador(colaborador),
            )?.getTime() ?? Infinity,
        }))
        .sort(
          (a, b) =>
            a.selecionado - b.selecionado ||
            a.prioridade - b.prioridade ||
            a.fimOcupacao - b.fimOcupacao ||
            String(a.colaborador.nome || "").localeCompare(
              String(b.colaborador.nome || ""),
            ),
        )
        .map(({ colaborador }) => colaborador);
    },
    etapaRegistroAtribuicao() {
      const etapaId = Number(this.ticketAtribuicao?.etapa?.id);
      if (!this.ticketAtribuicao || !Number.isFinite(etapaId)) return null;
      return (this.ticketAtribuicao.etapas || []).find(
        (etapa) => Number(etapa.etapaId || etapa.id) === etapaId,
      );
    },
    colaboradoresAtuaisAtribuicao() {
      return this.colaboradoresAtribuicaoSelecionados
        .map((id) => this.buscarColaboradorGeralPorId(id))
        .filter(Boolean)
        .sort((a, b) =>
          String(a.nome || "").localeCompare(String(b.nome || "")),
        );
    },
    previsaoFimAtribuicao() {
      const inicio = this.parseDataHora(this.previsaoInicioAtribuicao);
      const sla = Number(
        this.ticketAtribuicao?.etapa?.sla ||
          this.ticketAtribuicao?.etapa?.tempoSla ||
          this.ticketAtribuicao?.etapa?.slaMinutos ||
          0,
      );
      const fimSalvo = this.parseDataHora(
        this.etapaRegistroAtribuicao?.previsaoFim,
      );
      if (!inicio) return fimSalvo;
      if (!sla) return fimSalvo;
      return new Date(inicio.getTime() + sla * 60000);
    },
    janelaAtividadeAtribuicao() {
      const inicio = this.parseDataHora(this.previsaoInicioAtribuicao);
      const fim = this.parseDataHora(this.previsaoFimAtribuicao);
      return inicio && fim ? { inicio, fim } : null;
    },
    previsaoFimAtribuicaoExibicao() {
      return this.previsaoFimAtribuicao
        ? this.formatarDataHora(this.previsaoFimAtribuicao)
        : "";
    },
    colaboradoresDaAlocacao() {
      const atualizacaoDisponibilidade = this.disponibilidadeAtualizacao;
      const colaboradoresPorId = new Map();

      this.alocacoesFiltradasPorEtapa.forEach((alocacao) => {
        (alocacao.colaboradores || []).forEach((colaborador) => {
          const atual = colaboradoresPorId.get(colaborador.id) || {
            ...colaborador,
            alocacoes: [],
            etapas: [],
            alocacoesNomes: "",
            etapasNomes: "",
          };

          if (!atual.alocacoes.some((item) => item.id === alocacao.id)) {
            atual.alocacoes.push({ id: alocacao.id, nome: alocacao.nome });
          }

          (alocacao.etapas || []).forEach((etapa) => {
            if (
              (!this.etapaFiltroIds.length ||
                this.etapaFiltroIds.includes(etapa.id)) &&
              !atual.etapas.some((item) => item.id === etapa.id)
            ) {
              atual.etapas.push(etapa);
            }
          });

          atual.alocacoesNomes = atual.alocacoes
            .map((item) => item.nome)
            .join(", ");
          atual.etapasNomes = atual.etapas.map((item) => item.nome).join(", ");
          colaboradoresPorId.set(colaborador.id, atual);
        });
      });

      return Array.from(colaboradoresPorId.values())
        .map((colaborador) =>
          this.montarStatusColaborador(
            colaborador,
            null,
            atualizacaoDisponibilidade,
          ),
        )
        .filter((colaborador) => {
          if (this.filtroDisponibilidade === "disponivel")
            return !colaborador.indisponivel;
          if (this.filtroDisponibilidade === "indisponivel")
            return colaborador.indisponivel;
          return true;
        })
        .sort((a, b) =>
          String(a.nome || "").localeCompare(String(b.nome || "")),
        );
    },
    alocacoesGerenciamentoFormatadas() {
      return this.alocacoesGerenciamento.map((item) => ({
        ...item,
        etapasResumo: (item.etapas || []).map((etapa) => etapa.nome).join(", "),
        quantidadeColaboradores: (item.colaboradores || []).length,
      }));
    },
    ticketsFunerariosFiltrados() {
      return this.aplicarFiltrosLocaisTickets(this.ticketsFunerarios);
    },
    ticketsFavoritosVisiveis() {
      const termo = String(this.buscaTicket || "").trim().toLowerCase();
      return this.ticketsFavoritos.filter((ticket) => {
        if (
          this.etapaFiltroIds.length &&
          !this.etapaFiltroIds.includes(ticket.etapa?.id)
        ) {
          return false;
        }
        if (!termo) return true;
        return [
          ticket.nomeFalecido,
          ticket.cliente,
          ticket.contrato,
          ticket.tipoContrato,
          ticket.etapa?.nome,
          ticket.idAtendimento,
          ticket.id,
        ].some((valor) => String(valor ?? "").toLowerCase().includes(termo));
      });
    },
    ticketsFavoritosFiltrados() {
      return this.aplicarFiltrosLocaisTickets(this.ticketsFavoritosVisiveis);
    },
    idsAtendimentoFavoritos() {
      return new Set(this.ticketsFavoritos.map((ticket) => ticket.idAtendimento));
    },
    ticketsAtrasadosCount() {
      const atualizacao = this.ticketSlaAtualizacao;
      return [...this.ticketsFavoritosVisiveis, ...this.ticketsFunerarios].filter(
        (ticket) =>
          (!this.filtroSomenteEtapasSemAtribuicao ||
            this.ticketPossuiEtapaSemAtribuicao(ticket)) &&
          this.obterStatusOperacionalTicket(ticket, atualizacao).slaEstourado,
      ).length;
    },
    ticketsFunerariosComStatus() {
      return this.mapearTicketsComStatus(this.ticketsFunerariosFiltrados);
    },
    secoesTicketsAlocacao() {
      const favoritos = this.mapearTicketsComStatus(this.ticketsFavoritosFiltrados);
      if (!favoritos.length) {
        return [{ chave: "todos", itens: this.ticketsFunerariosComStatus }];
      }
      return [
        {
          chave: "favoritos",
          titulo: "Favoritos",
          icone: "mdi-star",
          cor: "amber darken-1",
          itens: favoritos,
        },
        {
          chave: "todos",
          titulo: "Demais tickets",
          icone: "mdi-format-list-bulleted",
          cor: "blue-grey",
          itens: this.ticketsFunerariosComStatus,
        },
      ];
    },
  },

  watch: {
    etapasSelecionadas(lista) {
      this.form.etapaIds = lista.map((item) => item.id);
    },
    colaboradoresSelecionados(lista) {
      this.form.colaboradorIds = lista.map((item) => item.id);
    },
    buscaTicket() {
      if (this.buscaTicketTimeout) {
        clearTimeout(this.buscaTicketTimeout);
      }
      this.buscaTicketTimeout = setTimeout(() => {
        this.paginaTickets = 0;
        this.paginaTicketsVisual = 1;
        if (this.fluxoSelecionado === "funeraria") {
          this.carregarTicketsFunerariosRecentes();
        }
      }, 350);
    },
  },

  async mounted() {
    await this.carregarTelaInicial();
    this.intervaloDisponibilidade = setInterval(() => {
      this.disponibilidadeAtualizacao += 1;
    }, 60000);
    this.intervaloTicketSla = setInterval(() => {
      this.ticketSlaAtualizacao += 1;
    }, 60000);
  },

  beforeDestroy() {
    if (this.intervaloDisponibilidade) {
      clearInterval(this.intervaloDisponibilidade);
    }
    if (this.intervaloTicketSla) {
      clearInterval(this.intervaloTicketSla);
    }
    if (this.buscaTicketTimeout) {
      clearTimeout(this.buscaTicketTimeout);
    }
  },

  methods: {
    obrigatorio(value) {
      return (
        (value !== null &&
          value !== undefined &&
          String(value).trim() !== "") ||
        "Este campo é obrigatório"
      );
    },

    mostrarMensagem(texto, tipo = "success") {
      this.mensagem = texto;
      this.tipoMensagem = tipo;
    },

    extrairMensagemErro(error) {
      const data = error?.response?.data;
      if (!data) return "Não foi possível concluir a operação.";
      if (typeof data === "string") return data;
      return (
        data.message || data.mensagem || "Não foi possível concluir a operação."
      );
    },

    formatarDataISO(data) {
      const ano = data.getFullYear();
      const mes = String(data.getMonth() + 1).padStart(2, "0");
      const dia = String(data.getDate()).padStart(2, "0");
      return `${ano}-${mes}-${dia}`;
    },

    formatarSla(etapa) {
      if (!etapa) return "-";
      const sla = etapa.sla || etapa.tempoSla || etapa.slaMinutos;
      return sla ? `${sla}m` : "-";
    },

    abrirDialogEscala(colaborador) {
      this.colaboradorEscalaSelecionado = colaborador;
      this.dialogEscala = true;
    },

    fecharDialogEscala() {
      this.dialogEscala = false;
      this.colaboradorEscalaSelecionado = null;
    },

    formatarPeriodoEscala(entrada, saida) {
      if (!entrada || !saida) return "-";
      return `${entrada} - ${saida}`;
    },

    ticketAgendado(ticket) {
      return String(ticket?.etapa?.tipo || "").toLowerCase() === "agendado";
    },

    primeiraEtapaEmAndamento() {
      return this.etapasFunerariasEmAndamento[0] || null;
    },

    async avancarTicketAgendado(ticketInfo) {
      const ticket = ticketInfo?.ticket;
      const proximaEtapa = this.primeiraEtapaEmAndamento();
      if (!ticket || !proximaEtapa) return;

      const ticketId = ticket.idAtendimento || ticket.id;
      this.avancandoTicketId = ticketId;
      try {
        const payload = JSON.parse(JSON.stringify(ticket));
        payload.etapa = {
          ...(payload.etapa || {}),
          id: proximaEtapa.id,
        };

        await FunerariaService.atualizar_servico(ticket.id, payload);
        await Promise.all([
          this.carregarTicketsFunerariosRecentes(),
          this.carregarOcupacoesColaboradores(),
        ]);
      } catch (error) {
        this.mostrarMensagem(this.extrairMensagemErro(error), "error");
      } finally {
        this.avancandoTicketId = null;
      }
    },

    abrirDialogAtribuicao(ticketInfo, etapaSelecionada = null) {
      const ticket = ticketInfo?.ticket || null;

      this.ticketAtribuicao = ticket
        ? {
            ...ticket,
            etapa: etapaSelecionada || ticket.etapa,
          }
        : null;
      const registro = this.obterRegistroEtapaTicket(
        this.ticketAtribuicao,
        this.ticketAtribuicao?.etapa,
      );
      console.log(registro);
      const previsaoInicio =
        registro?.previsaoInicio ||
        this.calcularPrevisaoInicioEtapa(
          this.ticketAtribuicao,
          this.ticketAtribuicao?.etapa,
        ) ||
        new Date();
      this.previsaoInicioAtribuicao =
        this.formatarDataHoraInput(previsaoInicio);
      this.colaboradoresAtribuicaoSelecionados = [
        ...(registro?.colaboradores || []),
      ];
      this.buscaColaboradorAtribuicao = "";
      this.filtroAtribuicaoDisponibilidade = "todos";
      this.dialogAtribuicao = true;
    },

    fecharDialogAtribuicao() {
      if (this.atribuindoColaborador) return;
      this.dialogAtribuicao = false;
      this.ticketAtribuicao = null;
      this.previsaoInicioAtribuicao = "";
      this.colaboradoresAtribuicaoSelecionados = [];
      this.buscaColaboradorAtribuicao = "";
      this.filtroAtribuicaoDisponibilidade = "todos";
      this.colaboradorAtribuindoId = null;
    },

    colaboradorSelecionadoAtribuicao(colaboradorId) {
      return this.colaboradoresAtribuicaoSelecionados.some(
        (id) => Number(id) === Number(colaboradorId),
      );
    },

    iconeAcaoAtribuicao(colaborador) {
      if (colaborador?.emFerias) return "mdi-beach";
      return this.colaboradorSelecionadoAtribuicao(colaborador?.id)
        ? "mdi-check"
        : "mdi-account-plus";
    },

    textoAcaoAtribuicao(colaborador) {
      if (colaborador?.emFerias) return "Férias";
      return this.colaboradorSelecionadoAtribuicao(colaborador?.id)
        ? "Selecionado"
        : "Adicionar";
    },

    alternarColaboradorAtribuicao(colaborador) {
      if (!colaborador) return;
      if (colaborador.emFerias) return;
      const colaboradorId = Number(colaborador.id);
      if (this.colaboradorSelecionadoAtribuicao(colaboradorId)) {
        this.removerColaboradorAtribuicao(colaboradorId);
        return;
      }

      const motivos = this.obterMotivosHoraExtra(colaborador);
      if (motivos.length) {
        this.confirmacaoHoraExtra = { colaborador, motivos };
        this.dialogConfirmacaoHoraExtra = true;
        return;
      }
      this.adicionarColaboradorAtribuicao(colaboradorId);
    },

    adicionarColaboradorAtribuicao(colaboradorId) {
      if (this.colaboradorSelecionadoAtribuicao(colaboradorId)) return;
      this.colaboradoresAtribuicaoSelecionados = [
        ...this.colaboradoresAtribuicaoSelecionados,
        Number(colaboradorId),
      ];
    },

    obterMotivosHoraExtra(colaborador) {
      const motivos = colaborador?.indisponivel
        ? ["Indisponível no horário atual da escala"]
        : [];
      (colaborador?.statusInfo?.alertas || [])
        .filter((alerta) => alerta.geraHoraExtra)
        .forEach((alerta) => motivos.push(alerta.descricao));
      return motivos;
    },

    confirmarAdicaoComHoraExtra() {
      const colaborador = this.confirmacaoHoraExtra.colaborador;
      if (colaborador) this.adicionarColaboradorAtribuicao(colaborador.id);
      this.fecharConfirmacaoHoraExtra();
    },

    fecharConfirmacaoHoraExtra() {
      this.dialogConfirmacaoHoraExtra = false;
      this.confirmacaoHoraExtra = { colaborador: null, motivos: [] };
    },

    removerColaboradorAtribuicao(colaboradorId) {
      this.colaboradoresAtribuicaoSelecionados =
        this.colaboradoresAtribuicaoSelecionados.filter(
          (id) => Number(id) !== Number(colaboradorId),
        );
    },

    async salvarAtribuicaoEtapa() {
      if (!this.ticketAtribuicao || !this.ticketAtribuicao.etapa) return;

      this.atribuindoColaborador = true;
      try {
        await FunerariaService.salvar_atribuicao_etapa(
          this.ticketAtribuicao.id,
          {
            etapaId: this.ticketAtribuicao.etapa.id,
            previsaoInicio: this.normalizarDataHoraApi(
              this.previsaoInicioAtribuicao,
            ),
            previsaoFim: this.normalizarDataHoraApi(this.previsaoFimAtribuicao),
            colaboradores: this.colaboradoresAtribuicaoSelecionados,
          },
        );
        this.atribuindoColaborador = false;
        this.fecharDialogAtribuicao();
        await Promise.all([
          this.carregarTicketsFunerariosRecentes(),
          this.carregarOcupacoesColaboradores(),
        ]);
      } catch (error) {
        this.mostrarMensagem(this.extrairMensagemErro(error), "error");
      } finally {
        this.atribuindoColaborador = false;
        this.colaboradorAtribuindoId = null;
      }
    },
    colaboradorEmHorarioEscala(colaborador) {
      const agora = this.obterAgoraSaoPaulo();
      const escalaHoje = (colaborador?.escala || []).filter(
        (escala) =>
          escala && escala.dia === agora.diaSolides && escala.inativa !== true,
      );

      return escalaHoje.some(
        (escala) =>
          this.horarioDentroDoIntervalo(
            agora.minutos,
            escala.entrada1,
            escala.saida1,
          ) ||
          this.horarioDentroDoIntervalo(
            agora.minutos,
            escala.entrada2,
            escala.saida2,
          ),
      );
    },

    obterOcupacaoColaborador(colaborador) {
      const id = Number(colaborador?.id);
      if (!Number.isFinite(id)) return null;
      return this.ocupacoesPorColaborador.get(id) || null;
    },

    montarStatusColaborador(colaborador, janelaAtividade = null, _atualizacao) {
      const ocupacao = this.obterOcupacaoColaborador(colaborador);
      const emEscala = this.colaboradorEmHorarioEscala(colaborador);
      const status = resolverDisponibilidade({
        emFerias: !!colaborador.emFerias,
        emEscala,
        ocupado: !!ocupacao,
        alertasAtividade: janelaAtividade
          ? avaliarAtividadeNaEscala(
              colaborador.escala,
              janelaAtividade.inicio,
              janelaAtividade.fim,
            )
          : [],
      });

      return {
        ...colaborador,
        ocupado: !!ocupacao,
        disponivel: !status.indisponivel && !ocupacao,
        emEscala,
        indisponivel: status.indisponivel,
        statusDisponibilidade: status.label,
        statusInfo: {
          ...status,
          detalhe: this.montarDetalheStatusColaborador(
            colaborador,
            ocupacao,
            emEscala,
          ),
        },
      };
    },

    montarDetalheStatusColaborador(colaborador, ocupacao, emEscala) {
      if (colaborador?.emFerias) {
        return {
          tipo: "indisponibilidade",
          titulo: colaborador.motivoIndisponibilidade || "Férias",
        };
      }
      if (ocupacao) {
        return {
          tipo: "ocupacao",
          titulo: this.descricaoOcupacaoColaborador(colaborador),
          idAtendimento: ocupacao.idAtendimento,
          atividadeNome:
            ocupacao.atividadeNome || ocupacao.etapaNome || "Atividade",
          subtitulo: this.periodoOcupacaoColaborador(colaborador),
        };
      }
      if (!emEscala) return { tipo: "indisponibilidade", titulo: "Ausente" };
      return null;
    },

    descricaoOcupacaoColaborador(colaborador) {
      const ocupacao = this.obterOcupacaoColaborador(colaborador);
      if (!ocupacao) return "";
      const ticket = ocupacao.idAtendimento ? `#${ocupacao.idAtendimento}` : "";
      const atividade =
        ocupacao.atividadeNome || ocupacao.etapaNome || "Atividade";
      return `${ticket} - ${atividade}`.trim();
    },

    periodoOcupacaoColaborador(colaborador) {
      const ocupacao = this.obterOcupacaoColaborador(colaborador);
      if (!ocupacao) return "";
      const inicio = this.parseDataHora(ocupacao.dtInicio);
      const fim = this.obterFimOcupacao(ocupacao);
      const inicioTexto = inicio ? this.formatarDataHora(inicio) : "-";
      const fimTexto = fim ? this.formatarDataHora(fim) : "-";
      return `Início: ${inicioTexto} | Prev. fim: ${fimTexto}`;
    },

    obterFimOcupacao(ocupacao) {
      if (!ocupacao) return null;
      return (
        this.parseDataHora(ocupacao.previsaoFim) ||
        this.parseDataHora(ocupacao.dtFim) ||
        this.calcularPrevisaoFimOcupacao(
          ocupacao,
          this.parseDataHora(ocupacao.dtInicio),
        )
      );
    },

    calcularPrevisaoFimOcupacao(ocupacao, inicio) {
      if (!ocupacao || !inicio) return null;
      const etapa = this.etapas.find(
        (item) => Number(item.id) === Number(ocupacao.etapaId),
      );
      return this.somarMinutos(inicio, etapa?.sla);
    },

    ticketPossuiEtapaSemAtribuicao(ticket) {
      const registros = ticket?.etapas || [];
      return this.etapasFunerariasEmAndamento.some((etapa) => {
        const registro = registros.find(
          (item) => Number(item.etapaId || item.id) === Number(etapa.id),
        );
        return !registro?.colaboradores?.length;
      });
    },

    obterColaboradoresEtapaAtual(ticket) {
      if (!ticket || !ticket.etapa) return [];
      const etapaId = Number(ticket.etapa.id);
      const registro = (ticket.etapas || []).find(
        (item) => Number(item.etapaId || item.id) === etapaId,
      );
      if (
        !registro ||
        !registro.colaboradores ||
        !registro.colaboradores.length
      ) {
        return [];
      }
      return registro.colaboradores
        .map((id) => this.buscarColaboradorGeralPorId(id))
        .filter(Boolean)
        .sort((a, b) =>
          String(a.nome || "").localeCompare(String(b.nome || "")),
        );
    },

    obterAgoraSaoPaulo() {
      const partes = new Intl.DateTimeFormat("pt-BR", {
        timeZone: "America/Sao_Paulo",
        weekday: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).formatToParts(new Date());
      const weekday =
        partes.find((parte) => parte.type === "weekday")?.value || "";
      const hour = Number(
        partes.find((parte) => parte.type === "hour")?.value || 0,
      );
      const minute = Number(
        partes.find((parte) => parte.type === "minute")?.value || 0,
      );
      const dias = {
        dom: 1,
        seg: 2,
        ter: 3,
        qua: 4,
        qui: 5,
        sex: 6,
        sab: 7,
      };

      return {
        diaSolides: dias[weekday.substring(0, 3).toLowerCase()] || 0,
        minutos: hour * 60 + minute,
      };
    },

    horarioDentroDoIntervalo(minutosAgora, inicio, fim) {
      const minutosInicio = this.converterHorarioParaMinutos(inicio);
      const minutosFim = this.converterHorarioParaMinutos(fim);
      if (minutosInicio === null || minutosFim === null) return false;

      if (minutosFim < minutosInicio) {
        return minutosAgora >= minutosInicio || minutosAgora <= minutosFim;
      }

      return minutosAgora >= minutosInicio && minutosAgora <= minutosFim;
    },

    converterHorarioParaMinutos(horario) {
      if (!horario || typeof horario !== "string") return null;
      const partes = horario.split(":");
      if (partes.length < 2) return null;
      const hora = Number(partes[0]);
      const minuto = Number(partes[1]);
      if (Number.isNaN(hora) || Number.isNaN(minuto)) return null;
      return hora * 60 + minuto;
    },

    obterStatusOperacionalTicket(ticket, _atualizacao) {
      if (!ticket || !ticket.etapa) {
        return this.criarStatusPadrao();
      }

      const etapaAtual = ticket.etapa;
      const slaMinutos = Number(
        etapaAtual.sla || etapaAtual.tempoSla || etapaAtual.slaMinutos || 0,
      );
      const agora = new Date();
      const registro = this.obterRegistroEtapaTicket(ticket, etapaAtual);
      const previsaoInicio =
        this.parseDataHora(registro?.previsaoInicio) ||
        this.calcularPrevisaoInicioEtapa(ticket, etapaAtual);
      const previsaoFim =
        this.parseDataHora(registro?.previsaoFim) ||
        this.somarMinutos(previsaoInicio, slaMinutos);
      const inicioRealizado = this.parseDataHora(registro?.dtInicio);
      const fimRealizado = this.parseDataHora(registro?.dtFim);
      const colaborador = this.obterColaboradorDaEtapa(registro);
      const atrasoMinutos =
        previsaoFim && !fimRealizado
          ? Math.max(0, Math.floor((agora - previsaoFim) / 60000))
          : 0;

      return {
        status: inicioRealizado
          ? fimRealizado
            ? "Finalizada"
            : "Em andamento"
          : "Aguardando inicio",
        colaborador,
        inicio: inicioRealizado || previsaoInicio,
        previsaoInicio,
        previsaoFim,
        referenciaSla: "previsao",
        tempoDecorridoMinutos:
          inicioRealizado && !fimRealizado
            ? Math.max(0, Math.floor((agora - inicioRealizado) / 60000))
            : 0,
        tempoRestanteMinutos:
          previsaoFim && !fimRealizado
            ? Math.max(0, Math.floor((previsaoFim - agora) / 60000))
            : 0,
        atrasoMinutos,
        slaMinutos,
        slaEstourado: atrasoMinutos > 0,
      };
    },

    criarStatusPadrao() {
      return {
        status: "Aguardando início",
        colaborador: null,
        inicio: null,
        referenciaSla: "dataAtendimento",
        tempoDecorridoMinutos: 0,
        tempoRestanteMinutos: 0,
        atrasoMinutos: 0,
        slaMinutos: 0,
        slaEstourado: false,
      };
    },

    obterColaboradorDaEtapa(etapaEmAndamento) {
      if (etapaEmAndamento?.colaborador) {
        return etapaEmAndamento.colaborador;
      }

      const colaboradorId = (etapaEmAndamento?.colaboradores || [])[0];
      if (!colaboradorId) {
        return null;
      }

      return this.buscarColaboradorGeralPorId(colaboradorId);
    },

    buscarColaboradorGeralPorId(colaboradorId) {
      const id = Number(colaboradorId);
      if (!Number.isFinite(id)) {
        return null;
      }

      const colaboradorGeral = this.colaboradoresAtivos.find(
        (item) => Number(item.id) === id,
      );
      if (colaboradorGeral) {
        return colaboradorGeral;
      }

      for (const alocacao of this.alocacoesDoFluxo) {
        const colaborador = (alocacao.colaboradores || []).find(
          (item) => Number(item.id) === id,
        );
        if (colaborador) {
          return colaborador;
        }
      }

      return null;
    },

    obterRegistroEtapaTicket(ticket, etapa) {
      const etapaId = Number(etapa?.id || etapa?.etapaId);
      if (!ticket || !Number.isFinite(etapaId)) return null;
      return (ticket.etapas || []).find(
        (item) => Number(item.etapaId || item.id) === etapaId,
      );
    },

    calcularPrevisaoInicioEtapa(ticket, etapaAlvo) {
      const etapaAlvoId = Number(etapaAlvo?.id || etapaAlvo?.etapaId);
      if (!ticket || !Number.isFinite(etapaAlvoId)) return null;

      const etapasOrdenadas = [...this.etapasFunerariasEmAndamento];
      let previsaoAnterior = this.parseDataHora(ticket.dataAtendimento);

      for (const etapa of etapasOrdenadas) {
        const registro = this.obterRegistroEtapaTicket(ticket, etapa);
        const previsaoInicio =
          this.parseDataHora(registro?.previsaoInicio) || previsaoAnterior;
        const previsaoFim =
          this.parseDataHora(registro?.previsaoFim) ||
          this.somarMinutos(previsaoInicio, etapa.sla);

        if (Number(etapa.id) === etapaAlvoId) {
          return previsaoInicio;
        }

        previsaoAnterior = previsaoFim || previsaoAnterior;
      }

      return previsaoAnterior;
    },

    somarMinutos(data, minutos) {
      const inicio = this.parseDataHora(data);
      const quantidade = Number(minutos || 0);
      if (!inicio || !quantidade) return inicio;
      return new Date(inicio.getTime() + quantidade * 60000);
    },

    parseDataHora(dataStr) {
      if (!dataStr) return null;
      if (dataStr instanceof Date) {
        return isNaN(dataStr.getTime()) ? null : dataStr;
      }

      const valor = String(dataStr).trim();
      const matchBR = valor.match(
        /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/,
      );
      if (matchBR) {
        const [, dia, mes, ano, hora = "00", minuto = "00", segundo = "00"] =
          matchBR;
        const dataBR = new Date(
          Number(ano),
          Number(mes) - 1,
          Number(dia),
          Number(hora),
          Number(minuto),
          Number(segundo),
        );
        return isNaN(dataBR.getTime()) ? null : dataBR;
      }

      const dataIso = new Date(valor);
      if (!isNaN(dataIso.getTime())) {
        return dataIso;
      }

      return null;
    },

    formatarTempoDecorrido(minutos) {
      if (minutos === null || minutos === undefined || minutos < 0) return "-";

      const horas = Math.floor(minutos / 60);
      const mins = Math.floor(minutos % 60);

      if (horas > 0) {
        return `${horas}h ${mins}m`;
      }
      return `${mins}m`;
    },

    formatarDataHora(data) {
      if (!data) return "-";
      const d = new Date(data);
      if (isNaN(d.getTime())) return "-";

      const dia = String(d.getDate()).padStart(2, "0");
      const mes = String(d.getMonth() + 1).padStart(2, "0");
      const hora = String(d.getHours()).padStart(2, "0");
      const minuto = String(d.getMinutes()).padStart(2, "0");

      return `${dia}/${mes} ${hora}:${minuto}`;
    },

    formatarDataHoraInput(data) {
      const d = this.parseDataHora(data);
      if (!d) return "";
      const ano = d.getFullYear();
      const mes = String(d.getMonth() + 1).padStart(2, "0");
      const dia = String(d.getDate()).padStart(2, "0");
      const hora = String(d.getHours()).padStart(2, "0");
      const minuto = String(d.getMinutes()).padStart(2, "0");
      return `${ano}-${mes}-${dia}T${hora}:${minuto}`;
    },

    normalizarDataHoraApi(data) {
      const valor = this.formatarDataHoraInput(data);
      return valor ? `${valor}:00` : null;
    },

    obterCorStatusOperacional(status) {
      const cores = {
        "Serviço solicitado": "#607D8B",
        "Aguardando início": "#FF9800",
        "Em andamento": "#4CAF50",
      };
      return cores[status] || "#9E9E9E";
    },

    async carregarTelaInicial() {
      await this.carregarAlocacoes();
      if (this.fluxoSelecionado) {
        await this.selecionarFluxo();
      }
    },

    async carregarAlocacoes() {
      this.loading = true;
      try {
        const response = await FluxoAlocacoesService.listar();
        this.alocacoes = response.data || [];
        if (this.fluxoSelecionado && !this.alocacoesDoFluxo.length) {
          this.fluxoSelecionado = null;
          this.etapaFiltroIds = [];
        }
      } catch (error) {
        this.mostrarMensagem(this.extrairMensagemErro(error), "error");
      } finally {
        this.loading = false;
      }
    },

    async selecionarFluxo() {
      this.buscaColaborador = "";
      this.buscaTicket = "";
      this.etapaFiltroIds = [];
      this.paginaTickets = 0;
      this.paginaTicketsVisual = 1;
      if (this.fluxoSelecionado === "funeraria") {
        await Promise.all([
          this.carregarEtapas(this.fluxoSelecionado),
          this.carregarColaboradoresAtivos(),
          this.carregarTicketsFunerariosRecentes(),
          this.carregarOcupacoesColaboradores(),
        ]);
      } else {
        this.etapas = [];
        this.ticketsFunerarios = [];
        this.ticketsFavoritos = [];
        this.ocupacoesColaboradores = [];
        this.totalTicketsFunerarios = 0;
        this.totalPaginasTickets = 0;
      }
    },

    aplicarFiltrosLocaisTickets(lista) {
      const atualizacao = this.ticketSlaAtualizacao;
      return lista.filter((ticket) => {
        if (
          this.filtroSomenteEtapasSemAtribuicao &&
          !this.ticketPossuiEtapaSemAtribuicao(ticket)
        ) {
          return false;
        }
        if (!this.filtroTicketsAtrasados) return true;
        const status = this.obterStatusOperacionalTicket(ticket, atualizacao);
        return status.slaEstourado;
      });
    },

    mapearTicketsComStatus(lista) {
      const atualizacao = this.ticketSlaAtualizacao;
      return lista.map((ticket) => ({
        ticket,
        status: this.obterStatusOperacionalTicket(ticket, atualizacao),
      }));
    },

    ehFavorito(ticket) {
      return this.idsAtendimentoFavoritos.has(ticket?.idAtendimento);
    },

    async carregarTicketsFavoritos() {
      try {
        const response = await FunerariaService.listar_favoritos({
          tipoData: "abertos",
        });
        this.ticketsFavoritos = response.data || [];
      } catch (error) {
        this.ticketsFavoritos = [];
        this.mostrarMensagem(this.extrairMensagemErro(error), "error");
      }
    },

    async alternarFavorito(ticket) {
      const idAtendimento = ticket?.idAtendimento;
      if (!idAtendimento || this.favoritandoId) return;

      this.favoritandoId = idAtendimento;
      try {
        if (this.ehFavorito(ticket)) {
          await FunerariaService.remover_favorito(idAtendimento);
        } else {
          await FunerariaService.adicionar_favorito(idAtendimento);
        }
        await this.carregarTicketsFunerariosRecentes();
      } catch (error) {
        this.mostrarMensagem(this.extrairMensagemErro(error), "error");
      } finally {
        this.favoritandoId = null;
      }
    },

    async abrirDialogCompartilharFavorito(ticket) {
      if (!this.ehFavorito(ticket)) return;
      this.ticketCompartilhamento = ticket;
      this.usuariosSelecionadosCompartilhamento = [];
      this.dialogCompartilharFavorito = true;
      this.carregandoUsuariosCompartilhamento = true;
      try {
        const response = await UsuarioService.list_simples();
        this.usuariosCompartilhamento = (response.data || []).sort((a, b) =>
          (a.nome || "").localeCompare(b.nome || "", "pt-BR"),
        );
      } catch (error) {
        this.usuariosCompartilhamento = [];
        this.mostrarMensagem(this.extrairMensagemErro(error), "error");
      } finally {
        this.carregandoUsuariosCompartilhamento = false;
      }
    },

    fecharDialogCompartilharFavorito() {
      this.dialogCompartilharFavorito = false;
      this.ticketCompartilhamento = null;
      this.usuariosSelecionadosCompartilhamento = [];
    },

    async compartilharFavorito() {
      const idAtendimento = this.ticketCompartilhamento?.idAtendimento;
      if (!idAtendimento || !this.usuariosSelecionadosCompartilhamento.length) {
        return;
      }

      this.compartilhandoFavorito = true;
      try {
        await FunerariaService.compartilhar_favorito(
          idAtendimento,
          this.usuariosSelecionadosCompartilhamento,
        );
        this.fecharDialogCompartilharFavorito();
        this.mostrarMensagem("Favorito compartilhado com sucesso.");
      } catch (error) {
        this.mostrarMensagem(this.extrairMensagemErro(error), "error");
      } finally {
        this.compartilhandoFavorito = false;
      }
    },

    async carregarTicketsFunerariosRecentes() {
      this.loadingTicketsFunerarios = true;
      try {
        const [response] = await Promise.all([
          FunerariaService.listarTicketsAlocacoes({
            etapaIds: this.etapaFiltroIds.length
              ? this.etapaFiltroIds.join(",")
              : undefined,
            busca: String(this.buscaTicket || "").trim() || undefined,
            ocultarFavoritos: true,
            page: this.paginaTickets,
            size: this.tamanhoPaginaTickets,
          }),
          this.carregarTicketsFavoritos(),
        ]);
        this.ticketsFunerarios = response.data?.content || [];
        this.totalTicketsFunerarios = response.data?.totalElements || 0;
        this.totalPaginasTickets = response.data?.totalPages || 0;
        this.paginaTickets = response.data?.number || 0;
        this.paginaTicketsVisual = this.paginaTickets + 1;
      } catch (error) {
        this.ticketsFunerarios = [];
        this.totalTicketsFunerarios = 0;
        this.totalPaginasTickets = 0;
        this.mostrarMensagem(this.extrairMensagemErro(error), "error");
      } finally {
        this.loadingTicketsFunerarios = false;
      }
    },

    async carregarOcupacoesColaboradores() {
      this.loadingOcupacoesColaboradores = true;
      try {
        const hoje = new Date();
        const inicio = new Date(hoje);
        inicio.setDate(hoje.getDate() - 7);
        const response = await FunerariaService.listar_ocupacoes_colaboradores({
          dataInicio: this.formatarDataISO(inicio),
          dataFim: this.formatarDataISO(hoje),
        });
        this.ocupacoesColaboradores = response.data || [];
      } catch (error) {
        this.ocupacoesColaboradores = [];
        this.mostrarMensagem(this.extrairMensagemErro(error), "error");
      } finally {
        this.loadingOcupacoesColaboradores = false;
      }
    },

    aoAlterarPaginaTickets(pagina) {
      this.paginaTickets = pagina - 1;
      this.carregarTicketsFunerariosRecentes();
    },

    abrirDialogAdicionar() {
      this.editando = false;
      this.limparForm();
      this.dialogTab = 0;
      this.dialog = true;
      this.carregarColaboradoresAtivos();
      this.$nextTick(() => this.$refs.formAlocacao?.resetValidation());
    },

    async abrirDialogEditar(alocacao) {
      if (!alocacao) return;
      this.editando = true;
      this.limparForm();
      this.dialogTab = 0;
      this.dialog = true;
      await Promise.all([
        this.carregarEtapas(alocacao.fluxo),
        this.carregarColaboradoresAtivos(),
      ]);

      this.form = {
        id: alocacao.id,
        nome: alocacao.nome,
        fluxo: alocacao.fluxo,
        etapaIds: alocacao.etapas.map((item) => item.id),
        colaboradorIds: alocacao.colaboradores.map((item) => item.id),
      };
      this.etapasSelecionadas = this.etapas.filter((item) =>
        this.form.etapaIds.includes(item.id),
      );
      this.colaboradoresSelecionados = this.colaboradoresAtivos.filter((item) =>
        this.form.colaboradorIds.includes(item.id),
      );
      this.$nextTick(() => this.$refs.formAlocacao?.resetValidation());
    },

    fecharDialog() {
      if (this.salvando) return;
      this.dialog = false;
      this.limparForm();
    },

    limparForm() {
      this.form = {
        id: null,
        nome: "",
        fluxo: null,
        etapaIds: [],
        colaboradorIds: [],
      };
      this.etapas = [];
      this.etapasSelecionadas = [];
      this.colaboradoresSelecionados = [];
      this.buscaDialogColaborador = "";
      this.dialogTab = 0;
    },

    async aoAlterarFluxo(fluxo) {
      this.form.etapaIds = [];
      this.form.colaboradorIds = [];
      this.etapasSelecionadas = [];
      this.colaboradoresSelecionados = [];
      this.dialogTab = 0;
      await this.carregarEtapas(fluxo);
    },

    async carregarEtapas(fluxo) {
      this.etapas = [];
      if (!fluxo) return;

      this.loadingEtapas = true;
      try {
        const response = await FluxoAlocacoesService.listarEtapas(fluxo);
        this.etapas = response.data || [];
      } catch (error) {
        this.mostrarMensagem(this.extrairMensagemErro(error), "error");
      } finally {
        this.loadingEtapas = false;
      }
    },

    async carregarColaboradoresAtivos() {
      if (this.colaboradoresAtivos.length) return;

      this.loadingColaboradores = true;
      try {
        const response =
          await FluxoAlocacoesService.listarColaboradoresAtivos();
        this.colaboradoresAtivos = response.data || [];
      } catch (error) {
        this.mostrarMensagem(this.extrairMensagemErro(error), "error");
      } finally {
        this.loadingColaboradores = false;
      }
    },

    validarSelecoes() {
      if (!this.form.etapaIds.length) {
        this.dialogTab = 0;
        this.mostrarMensagem(
          "Para salvar, é obrigatório selecionar pelo menos uma etapa.",
          "warning",
        );
        return false;
      }
      if (!this.form.colaboradorIds.length) {
        this.dialogTab = 1;
        this.mostrarMensagem(
          "Para salvar, é obrigatório selecionar pelo menos um colaborador.",
          "warning",
        );
        return false;
      }
      return true;
    },

    async salvar() {
      const formOk = this.$refs.formAlocacao?.validate();
      if (!formOk || !this.validarSelecoes()) return;

      this.salvando = true;
      const payload = {
        nome: this.form.nome,
        fluxo: this.form.fluxo,
        etapaIds: this.form.etapaIds,
        colaboradorIds: this.form.colaboradorIds,
      };

      try {
        const response = this.editando
          ? await FluxoAlocacoesService.editar(this.form.id, payload)
          : await FluxoAlocacoesService.criar(payload);

        this.dialog = false;
        this.fluxoSelecionado = response.data.fluxo;
        this.etapaFiltroIds = [];
        await this.carregarAlocacoes();
        if (this.dialogGerenciar) {
          await this.carregarAlocacoesGerenciamento();
        }
        this.mostrarMensagem("Alocação salva com sucesso.");
      } catch (error) {
        this.mostrarMensagem(this.extrairMensagemErro(error), "error");
      } finally {
        this.salvando = false;
      }
    },

    confirmarExclusao(alocacao) {
      this.alocacaoParaExcluir = alocacao;
      this.dialogExcluir = true;
    },

    async excluir() {
      if (!this.alocacaoParaExcluir) return;

      this.excluindo = true;
      try {
        await FluxoAlocacoesService.excluir(this.alocacaoParaExcluir.id);
        this.dialogExcluir = false;
        this.alocacaoParaExcluir = null;
        await this.carregarAlocacoes();
        if (this.dialogGerenciar) {
          await this.carregarAlocacoesGerenciamento();
        }
        this.mostrarMensagem("Alocação excluída com sucesso.");
      } catch (error) {
        this.mostrarMensagem(this.extrairMensagemErro(error), "error");
      } finally {
        this.excluindo = false;
      }
    },

    alternarFiltroEtapa(etapa) {
      if (!etapa || !etapa.id) return;
      if (this.etapaFiltroIds.includes(etapa.id)) {
        this.etapaFiltroIds = this.etapaFiltroIds.filter(
          (id) => id !== etapa.id,
        );
      } else {
        this.etapaFiltroIds = [...this.etapaFiltroIds, etapa.id];
      }
      this.paginaTickets = 0;
      this.paginaTicketsVisual = 1;
      if (this.fluxoSelecionado === "funeraria") {
        this.carregarTicketsFunerariosRecentes();
      }
    },

    limparFiltroEtapas() {
      this.etapaFiltroIds = [];
      this.paginaTickets = 0;
      this.paginaTicketsVisual = 1;
      if (this.fluxoSelecionado === "funeraria") {
        this.carregarTicketsFunerariosRecentes();
      }
    },

    async abrirGerenciamento() {
      this.dialogGerenciar = true;
      await this.carregarAlocacoesGerenciamento();
    },

    async carregarAlocacoesGerenciamento() {
      this.loadingGerenciamento = true;
      try {
        const response = await FluxoAlocacoesService.listarTodas();
        this.alocacoesGerenciamento = response.data || [];
      } catch (error) {
        this.mostrarMensagem(this.extrairMensagemErro(error), "error");
      } finally {
        this.loadingGerenciamento = false;
      }
    },
  },
};
</script>

<style scoped>
.fluxo-alocacoes-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 100%;
  padding: 16px;
  background-color: var(--app-bg);
}

@media (min-width: 640px) {
  .fluxo-alocacoes-page {
    padding: 24px;
  }
}

.busca-tabela {
  width: 260px;
  max-width: 100%;
  flex: 0 0 auto;
}

/* ── Filtros ── */
.filtro-fluxo {
  max-width: 360px;
}

.filtro-etapas {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
}

.filtro-etapas__label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
}

.filtro-etapas__lista {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.etapa-filtro {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #475569;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s, color 0.15s;
}

.etapa-filtro .pi {
  font-size: 0.5rem;
  color: var(--etapa-cor);
}

.etapa-filtro:hover {
  border-color: #94a3b8;
}

.etapa-filtro--ativa,
.etapa-filtro--ativa:hover {
  border-color: var(--etapa-cor);
  color: #fff;
  background: var(--etapa-cor);
}

.etapa-filtro--ativa .pi {
  font-size: 0.65rem;
  color: #fff;
}

.filtros-tickets-switch {
  display: flex;
  align-items: center;
  height: 40px;
}

/* ── Tabelas ── */
.nome-colaborador {
  font-weight: 600;
  color: #1e293b;
}

.nome-colaborador-truncado {
  display: block;
  max-width: 200px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.escala-resumo {
  max-width: 360px;
  white-space: normal;
  line-height: 1.35;
  color: #334155;
}

.swatch {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}

/* ── Tickets ── */
.tickets-alocacoes-list {
  padding-top: 4px;
}

.secao-tickets + .secao-tickets {
  margin-top: 20px;
}

.secao-tickets--favoritos {
  padding: 12px 12px 4px;
  border: 1px solid #fde68a;
  border-radius: 12px;
  background: #fffbeb;
}

.secao-tickets-titulo {
  display: flex;
  align-items: center;
  margin: 0 0 12px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #475569;
}

.secao-tickets-linha {
  flex: 1;
  height: 1px;
  margin-left: 12px;
  background: #e2e8f0;
}

.tickets-panels {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ticket-panel {
  margin-top: 0 !important;
  border: 1px solid #e2e8f0;
  border-radius: 8px !important;
  background: #fff !important;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
  transition: box-shadow 0.15s;
}

.ticket-panel:hover {
  box-shadow: 0 4px 6px -1px rgba(15, 23, 42, 0.1);
}

.ticket-panel::before {
  box-shadow: none !important;
}

.ticket-panel::after {
  display: none;
}

.ticket-panel__header {
  min-height: 64px !important;
  padding: 12px 16px !important;
}

.ticket-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
}

.ticket-card-title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.ticket-card-sla {
  flex: 0 0 auto;
}

.ticket-id {
  font-size: 0.875rem;
  font-weight: 700;
  color: #6366f1;
}

.ticket-falecido {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1e293b;
}

.ticket-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  color: #94a3b8;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;
}

.ticket-icon-btn:hover {
  color: #475569;
  background: #f1f5f9;
}

.ticket-icon-btn--favorito,
.ticket-icon-btn--favorito:hover {
  color: #f59e0b;
}

.ticket-icon-btn .pi {
  font-size: 0.875rem;
}

.ticket-info-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px 20px;
  margin-bottom: 16px;
  padding: 14px;
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  background: #f8fafc;
}

@media (max-width: 959px) {
  .ticket-info-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 599px) {
  .ticket-info-grid {
    grid-template-columns: 1fr;
  }
}

.ticket-label {
  display: block;
  margin-bottom: 4px;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #64748b;
}

.ticket-value {
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.4;
  color: #1e293b;
}

.ticket-value--tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.ticket-value--forte {
  font-weight: 700;
}

.ticket-value--erro {
  color: #dc2626;
}

.ticket-value--alerta {
  color: #ea580c;
}

.ticket-vazio {
  font-size: 0.75rem;
  color: #94a3b8;
}

.ticket-fluxo-titulo {
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
}

/* ── Diálogo de atribuição ── */
.atribuicao-resumo,
.card-secao-atribuicao {
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
}

.atribuicao-resumo {
  margin-bottom: 16px;
}

.atribuicao-datas {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.atribuidos-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.atribuidos-titulo,
.secao-atribuicao-titulo,
.dialog-tab-titulo {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e293b;
}

.area-colaboradores-atribuidos {
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 6px;
  min-height: 48px;
  padding: 10px;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  background: #fff;
}

.atribuidos-vazio {
  width: 100%;
  align-self: center;
  font-size: 0.8125rem;
  text-align: center;
  color: #94a3b8;
}

.secao-atribuicao-header {
  margin-bottom: 12px;
}

.secao-atribuicao-subtitulo {
  margin-top: 2px;
  font-size: 0.75rem;
  color: #64748b;
}

.secao-atribuicao-filtros {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
}

.pulse-dialog__footer--bordered {
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 0 0 12px 12px;
}

/* ── Diálogo de alocação ── */
.form-alocacao-grid {
  display: grid;
  grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
  gap: 12px;
}

.dialog-tabs-items {
  background: transparent !important;
}

.dialog-tab-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.gerenciamento-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
}

.gerenciamento-toolbar__acoes {
  display: flex;
  gap: 8px;
}

@media (max-width: 599px) {
  .ticket-card-header {
    display: block;
  }

  .ticket-card-sla {
    margin-top: 6px;
  }

  .atribuicao-datas,
  .form-alocacao-grid {
    grid-template-columns: 1fr;
  }
}
</style>
